import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, Megaphone, Moon, GraduationCap, CalendarX, ForkKnife, Check } from "@phosphor-icons/react";

const typeMeta = {
  announcement: { icon: Megaphone, color: "#FF9F0A" },
  sleep: { icon: Moon, color: "#BF5AF2" },
  grade: { icon: GraduationCap, color: "#FF3B30" },
  attendance: { icon: CalendarX, color: "#0A84FF" },
  meal: { icon: ForkKnife, color: "#34C759" },
};

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);
  const [items, setItems] = useState([]);
  const ref = useRef();

  const fetchCount = () => api.get("/notifications/unread/count").then((r) => setCount(r.data.count)).catch(() => {});
  const fetchList = () => api.get("/notifications").then((r) => setItems(r.data)).catch(() => {});

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next) fetchList();
  };

  const openItem = async (n) => {
    if (!n.read) { await api.post(`/notifications/${n.id}/read`); fetchCount(); }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const markAll = async () => { await api.post("/notifications/read-all"); setCount(0); fetchList(); };

  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notification-bell" className="relative text-[#A3A3A3] hover:text-white transition-colors p-1">
        <Bell size={22} weight={count > 0 ? "fill" : "bold"} />
        {count > 0 && (
          <span data-testid="notification-count" className="absolute -top-1 -right-1 bg-[#FF3B30] text-white text-[10px] font-bold rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel" className="absolute right-0 mt-2 w-80 md:w-96 bg-[#141414] border border-white/10 rounded-sm shadow-2xl z-50 max-h-[70vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <span className="font-head text-lg font-bold uppercase tracking-tight">Alerts</span>
            <button onClick={markAll} data-testid="mark-all-read" className="text-[11px] uppercase tracking-widest text-[#FF3B30] font-semibold flex items-center gap-1 hover:text-[#FF5C53]"><Check size={14} weight="bold" /> Mark all read</button>
          </div>
          <div className="overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-4 py-10 text-center text-sm text-[#737373]">You're all caught up.</div>
            ) : items.map((n) => {
              const m = typeMeta[n.type] || { icon: Bell, color: "#A3A3A3" };
              const Icon = m.icon;
              return (
                <button key={n.id} onClick={() => openItem(n)} data-testid={`notif-${n.id}`}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left border-b border-white/5 hover:bg-white/[0.03] transition-colors ${n.read ? "opacity-60" : ""}`}>
                  <div className="w-8 h-8 rounded-sm flex items-center justify-center shrink-0 mt-0.5" style={{ background: `${m.color}22` }}>
                    <Icon size={16} weight="fill" color={m.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white">{n.title}</div>
                    <div className="text-xs text-[#A3A3A3] truncate">{n.body}</div>
                    <div className="text-[10px] text-[#737373] mt-0.5">{timeAgo(n.created_at)}</div>
                  </div>
                  {!n.read && <span className="w-2 h-2 rounded-full bg-[#FF3B30] mt-2 shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
